import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Upload,
  message,
  Popconfirm,
  Image,
  Typography,
  Row,
  Col,
  Table,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { sliderService, Slider } from '../services/sliderService';
import { getImageUrl } from '../config/api';
import type { UploadProps } from 'antd';

const { Title } = Typography;

const Sliders: React.FC = () => {
  const [sliders, setSliders] = useState<Slider[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSlider, setEditingSlider] = useState<Slider | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      setLoading(true);
      const slidersData = await sliderService.getSliders();
      setSliders(Array.isArray(slidersData) ? slidersData.sort((a: Slider, b: Slider) => a.order - b.order) : []);
    } catch (error: any) {
      console.error('Error fetching sliders:', error);
      message.error(error.message || 'Failed to fetch sliders');
      setSliders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingSlider(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (slider: Slider) => {
    setEditingSlider(slider);
    form.setFieldsValue({
      redirectionUrl: slider.redirectionUrl || '',
      order: slider.order,
      isActive: slider.isActive,
      imageUrl: slider.imageUrl, // For display
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const formData: any = {
        redirectionUrl: values.redirectionUrl || '',
        order: values.order || 0,
        isActive: values.isActive !== undefined ? values.isActive : true,
      };

      // Handle file upload if new image is selected
      if (values.imageFile && values.imageFile.file) {
        formData.image = values.imageFile.file.originFileObj || values.imageFile.file;
      }

      if (editingSlider) {
        await sliderService.updateSlider(editingSlider._id, formData);
        message.success('Slider updated successfully');
      } else {
        if (!formData.image) {
          message.error('Please upload an image');
          return;
        }
        await sliderService.createSlider(formData);
        message.success('Slider created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      fetchSliders();
    } catch (error: any) {
      console.error('Error saving slider:', error);
      message.error(error.message || 'Failed to save slider');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await sliderService.toggleSlider(id);
      message.success('Slider status updated');
      fetchSliders();
    } catch (error: any) {
      message.error(error.message || 'Failed to toggle slider');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await sliderService.deleteSlider(id);
      message.success('Slider deleted successfully');
      fetchSliders();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete slider');
    }
  };

  const uploadProps: UploadProps = {
    name: 'image',
    listType: 'picture-card',
    maxCount: 1,
    beforeUpload: (file) => {
      // Store the file in form for later submission
      form.setFieldsValue({ imageFile: { file } });
      return false; // Prevent auto upload
    },
    onRemove: () => {
      form.setFieldsValue({ imageFile: null });
    },
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 150,
      render: (imageUrl: string) => (
        <Image
          src={getImageUrl(imageUrl || '')}
          alt="Slider"
          width={120}
          height={80}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback="https://via.placeholder.com/120x80"
        />
      ),
    },
    {
      title: 'Redirect URL',
      dataIndex: 'redirectionUrl',
      key: 'redirectionUrl',
      render: (url: string) => url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-word' }}>
          {url.length > 50 ? `${url.substring(0, 50)}...` : url}
        </a>
      ) : '-',
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 80,
      sorter: (a: Slider, b: Slider) => a.order - b.order,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean, record: Slider) => (
        <Switch 
          checked={isActive} 
          onChange={() => handleToggle(record._id)}
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (record: Slider) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this slider?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>Sliders</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchSliders}>
                Refresh
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add Slider
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={sliders}
          rowKey="_id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingSlider ? 'Edit Slider' : 'Add Slider'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingSlider(null);
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item 
            name="imageFile" 
            label="Image" 
            rules={editingSlider ? [] : [{ required: true, message: 'Please upload an image' }]}
          >
            <Upload {...uploadProps}>
              {editingSlider && form.getFieldValue('imageUrl') ? (
                <div>
                  <Image
                    src={getImageUrl(form.getFieldValue('imageUrl'))}
                    alt="Current slider"
                    width={200}
                    style={{ marginBottom: 8, borderRadius: 4 }}
                  />
                  <div>Click to change image</div>
                </div>
              ) : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item name="redirectionUrl" label="Redirect URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="order" label="Order" rules={[{ required: true, message: 'Please enter order' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" valuePropName="checked" initialValue={true} label="Status">
            <Switch /> Active
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Sliders;

